import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LuUserRound } from 'react-icons/lu';
import useStudentStore from '../../../store/studentStore';
import useSchoolStore from '../../../store/schoolStore';
import { getClassName } from '../../../utils/schoolUtils';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import PageHeader from '../../../components/common/PageHeader';
import InfoGrid, { InfoItem } from '../../../components/common/InfoGrid';
import Avatar from '../../../components/common/Avatar';
import Badge from '../../../components/common/Badge';
import Notice from '../../../components/common/Notice';
import EmptyState from '../../../components/common/EmptyState';
import ErrorWidget from '../../../components/common/ErrorWidget';
import LoadingMessage from '../../../components/common/LoadingMessage';
import UnsupportedFeatureDialog from '../../../components/utils/UnsupportedFeatureDialog';

const formatDate = (date) => {
  if (!date) return '';

  // Firestore Timestamp
  if (date.toDate && typeof date.toDate === 'function') {
    return date.toDate().toLocaleDateString('en-IN');
  }

  if (date instanceof Date) {
    return date.toLocaleDateString('en-IN');
  }

  if (typeof date === 'string') {
    return new Date(date).toLocaleDateString('en-IN');
  }

  return '';
};

const formatGender = (gender) => {
  if (gender === 'M') return 'Male';
  if (gender === 'F') return 'Female';
  return gender;
};

/**
 * One student's details, read-only. The student is taken from the list loaded on the
 * Students page (useStudentStore.students); editing is done in the SchoolLine app.
 */
const StudentDetailsPage = () => {
  const { studentId } = useParams();
  const [showUnsupportedDialog, setShowUnsupportedDialog] = useState(false);
  const [studentData, setStudentData] = useState(null);

  const { students, isStudentsLoading, studentsError } = useStudentStore();
  const { schoolId } = useSchoolStore();

  useEffect(() => {
    if (students && students.length > 0 && studentId) {
      const student = students.find(studentItem => studentItem.id === studentId || studentItem.userId === studentId);
      setStudentData(student);
    }
  }, [students, studentId]);

  const back = { to: `/dash/${schoolId}/students`, label: 'Students' };

  if (isStudentsLoading) {
    return <LoadingMessage message="Loading student details…" />;
  }

  if (studentsError) {
    return (
      <>
        <PageHeader title="Student details" back={back} />
        <ErrorWidget error={{ message: studentsError }} title="Could not load the student" />
      </>
    );
  }

  if (!studentData) {
    return (
      <>
        <PageHeader title="Student details" back={back} />
        <Card>
          <EmptyState
            icon={<LuUserRound />}
            title="Student not found"
            message="Open the Students page, choose the class and section, and pick the student again."
          />
        </Card>
      </>
    );
  }

  const student = studentData.student || studentData;
  const studentName = student.name || studentData.name;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={studentName || 'Student details'}
        back={back}
        actions={
          <Button variant="secondary" onClick={() => setShowUnsupportedDialog(true)}>
            Edit details
          </Button>
        }
      />

      <Notice tone="info" className="mb-5">
        Student details can be viewed here. To change them, use the SchoolLine app.
      </Notice>

      <Card className="mb-5">
        <div className="flex items-center gap-5">
          <Avatar name={studentName} src={student.profileUrl} size="xl" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-ui-ink">{studentName || '—'}</h2>
              <Badge tone={studentData.blocked ? 'red' : 'green'}>{studentData.blocked ? 'Blocked' : 'Active'}</Badge>
            </div>
            <InfoGrid columns={4} className="mt-3">
              <InfoItem label="Class" value={`${getClassName(schoolId, student.Class || studentData.class)} – ${student.sec || studentData.sec || ''}`} />
              <InfoItem label="Admission no." value={student.admissionNo || studentData.admissionNo} />
              <InfoItem label="Roll no." value={student.rollNumber} />
              <InfoItem label="Student ID" value={studentData.userId} />
            </InfoGrid>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card title="Personal">
          <InfoGrid columns={2}>
            <InfoItem label="Full name" value={studentName} />
            <InfoItem label="Gender" value={formatGender(student.gender)} />
            <InfoItem label="Date of birth" value={formatDate(student.dob)} />
            <InfoItem label="Age" value={student.age?.toString()} />
            <InfoItem label="Nationality" value={student.nationality} />
            <InfoItem label="Mother tongue" value={student.motherTongue} />
            <InfoItem label="Caste" value={student.caste} />
            <InfoItem label="Community" value={student.community} />
            <InfoItem label="Category" value={student.category} />
            <InfoItem label="Aadhaar number" value={student.aadhaarNo} />
            <InfoItem label="Ration card" value={student.rationCard} />
            <InfoItem label="Physically challenged" value={student.physicallyChallenged ? 'Yes' : 'No'} />
          </InfoGrid>
        </Card>

        <Card title="School">
          <InfoGrid columns={2}>
            <InfoItem label="Admission number" value={student.admissionNo || studentData.admissionNo} />
            <InfoItem label="Admission date" value={formatDate(student.admissionDate)} />
            <InfoItem label="Current class" value={getClassName(schoolId, student.Class || studentData.class)} />
            <InfoItem label="Section" value={student.sec || studentData.sec} />
            <InfoItem label="Admission class" value={getClassName(schoolId, student.admissionClass)} />
            <InfoItem label="Previous school" value={student.previousSchool} />
            <InfoItem label="Bus number" value={student.busNo} />
            <InfoItem label="Boarding place" value={student.boardingPlace} />
          </InfoGrid>
        </Card>

        <Card title="Contact and address">
          <InfoGrid columns={2}>
            <InfoItem label="Contact number" value={student.contactNo?.toString()} />
            <InfoItem label="House name" value={student.houseName} />
            <InfoItem label="Place" value={student.placeOfHouse} />
            <InfoItem label="Post office" value={student.postOffice} />
            <InfoItem label="Taluk" value={student.taluk} />
            <InfoItem label="PIN code" value={student.pinCode} />
            <InfoItem label="Full address" value={student.address} className="sm:col-span-2" />
          </InfoGrid>
        </Card>

        <Card title="Account">
          <InfoGrid columns={2}>
            <InfoItem label="User ID" value={studentData.userId} />
            <InfoItem label="Account type" value={studentData.type === 1 ? 'Student' : 'Other'} />
            <InfoItem label="Registered number" value={studentData.registeredNumber?.toString()} />
            <InfoItem label="Account status" value={studentData.blocked ? 'Blocked' : 'Active'} />
          </InfoGrid>
        </Card>

        <Card title="Family" className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <h3 className="mb-3 text-[13px] font-semibold text-ui-ink">Father</h3>
              <InfoGrid columns={1}>
                <InfoItem label="Name" value={student.fatherName || studentData.fathername} />
                <InfoItem label="Mobile" value={student.fatherMobNo?.toString()} />
                <InfoItem label="Occupation" value={student.fatherOccupation} />
              </InfoGrid>
            </div>
            <div>
              <h3 className="mb-3 text-[13px] font-semibold text-ui-ink">Mother</h3>
              <InfoGrid columns={1}>
                <InfoItem label="Name" value={student.motherName} />
                <InfoItem label="Mobile" value={student.motherMobNo?.toString()} />
                <InfoItem label="Occupation" value={student.motherOccupation} />
              </InfoGrid>
            </div>
            <div>
              <h3 className="mb-3 text-[13px] font-semibold text-ui-ink">Guardian</h3>
              <InfoGrid columns={1}>
                <InfoItem label="Name" value={student.guardian} />
                <InfoItem label="Relation" value={student.guardianRelation} />
                <InfoItem label="Mobile" value={student.guardianMobNo?.toString()} />
                <InfoItem label="Occupation" value={student.guardianOccupation} />
              </InfoGrid>
            </div>
          </div>

          {student.siblings && student.siblings.length > 0 && (
            <div className="mt-6 border-t border-ui-line-soft pt-5">
              <h3 className="mb-3 text-[13px] font-semibold text-ui-ink">Siblings</h3>
              <ul className="flex flex-wrap gap-2">
                {student.siblings.map((sibling, siblingIndex) => (
                  <li key={siblingIndex} className="rounded-lg bg-ui-muted px-3 py-2 text-sm text-ui-ink">
                    {sibling.name || '—'}
                    {sibling.class && <span className="text-ui-ink-3"> · Class {sibling.class}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      </div>

      <UnsupportedFeatureDialog
        isOpen={showUnsupportedDialog}
        onClose={() => setShowUnsupportedDialog(false)}
        feature="Student Data Editing"
      />
    </div>
  );
};

export default StudentDetailsPage;
